/**
 * Super Admin API — reads platform data via payment backend (Firebase Admin SDK).
 * Requires REACT_APP_BOOKAPT_ADMIN_API_KEY matching server BOOKAPT_ADMIN_API_KEY.
 */

import { STRIPE_CONFIG } from '../config/stripeConfig';

const API_BASE = STRIPE_CONFIG.apiEndpoint;

export function getAdminApiKey(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_BOOKAPT_ADMIN_API_KEY) {
    return String(process.env.REACT_APP_BOOKAPT_ADMIN_API_KEY).trim();
  }
  return '';
}

export function isAdminApiConfigured(): boolean {
  return getAdminApiKey().length > 0;
}

export interface AdminBusinessData {
  id: string;
  name: string;
  industry: string;
  status: 'active' | 'inactive' | 'suspended';
  plan: string;
  revenue: number;
  appointments: number;
  joinDate: string;
}

export interface AdminStats {
  totalUsers: number;
  totalBusinesses: number;
  activeBusinesses: number;
  totalAppointments: number;
  businessRevenue: number;
  totalCreditsPurchasedRevenue: number;
  businessSubscriptionRevenue: number;
}

export interface AdminDashboardResponse {
  success: boolean;
  businesses?: AdminBusinessData[];
  stats?: AdminStats;
  error?: string;
}

async function parseJson(res: Response) {
  return res.json().catch(() => ({}));
}

function adminHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-BookAPT-Admin-Key': getAdminApiKey(),
  };
}

export async function fetchAdminDashboardFromApi(): Promise<AdminDashboardResponse> {
  const adminKey = getAdminApiKey();
  if (!adminKey) {
    return {
      success: false,
      error:
        'Admin API key not configured. Set REACT_APP_BOOKAPT_ADMIN_API_KEY (same as server BOOKAPT_ADMIN_API_KEY).',
    };
  }

  try {
    const res = await fetch(`${API_BASE}/admin/dashboard`, {
      method: 'GET',
      headers: adminHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || `HTTP ${res.status}` };
    }
    return {
      success: true,
      businesses: data.businesses || [],
      stats: data.stats,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to load admin dashboard' };
  }
}
